<template>
  <a-modal
    :visible="props.visible"
    width="100%"
    wrap-class-name="table-modal"
    centered
    class="alarm-record"
    :closeIcon="true"
    :destroyOnClose="true"
    @cancel="closeModal"
  >
    <template #title>
      <div class="modal-name">报警记录</div>
      <a-button class="close-btn" @click="closeModal">关闭 </a-button>
    </template>
    <div class="modmian" style="padding: 0">
      <div class="search">
        <div class="search-left">
          <a-form
            :model="formState"
            style="width: 100%"
            :label-col="{ md: { span: 5 }, sm: { span: 24 } }"
            :wrapper-col="{ md: { span: 17 }, sm: { span: 24 } }"
          >
            <a-row>
              <a-col :lg="6" :md="12" :sm="24" :xs="24">
                <a-form-item label="类型">
                  <a-select
                    v-model:value="formState.type"
                    placeholder="请选择类型"
                    :options="typeOptions"
                    :allowClear="true"
                  >
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :lg="6" :md="12" :sm="24" :xs="24">
                <a-form-item label="设备名称">
                  <a-input
                    v-model:value="formState.deviceName"
                    placeholder="请输入设备名称"
                    :allowClear="true"
                  />
                </a-form-item>
              </a-col>
              <a-col :lg="6" :md="12" :sm="24" :xs="24">
                <a-form-item label="故障内容">
                  <a-input
                    v-model:value="formState.content"
                    placeholder="请输入故障内容"
                    :allowClear="true"
                  />
                </a-form-item>
              </a-col>
              <a-col :lg="6" :md="12" :sm="24" :xs="24">
                <a-form-item label="报警时间">
                  <a-range-picker
                    v-model:value="alarmTime"
                    :locale="locale"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                    :placeholder="['开始时间', '结束时间']"
                  />
                </a-form-item>
              </a-col>
            </a-row>
          </a-form>
        </div>
        <div class="search-right">
          <a-button type="primary" @click="handleSearch">
            <template #icon><SearchOutlined /></template>
            查询
          </a-button>
          <a-button type="primary" @click="resetSearch">
            <template #icon><RedoOutlined /></template>
            重置
          </a-button>
        </div>
      </div>
      <a-table
        rowKey="id"
        :columns="columns"
        :data-source="data"
        :loading="loading"
        :pagination="pagination"
        @change="pageChange"
      >
        <template v-slot:bodyCell="{ column, text, record }">
          <!-- 类别 -->
          <template v-if="column.dataIndex === 'type'">
            <span :class="getCategoryClass(record.type)">
              {{ record.type ?? "--" }}
            </span>
          </template>
          <template v-else>
            <a-tooltip placement="topLeft">
              <template #title>{{ text ?? "" }}</template>
              {{ text ?? "" }}
            </a-tooltip>
          </template>
        </template>
      </a-table>
    </div>
    <template #footer> </template>
  </a-modal>
</template>

<script setup>
import locale from "ant-design-vue/es/date-picker/locale/zh_CN";
import { ref, onMounted, defineEmits, defineProps } from "vue";
import { apiTopHeader } from "@/api/api-topHeader.js";
import { message } from "ant-design-vue";
import { SearchOutlined, RedoOutlined } from "@ant-design/icons-vue";

const emits = defineEmits(["update:visible"]);
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
    default: false,
  },
});

// 类别数据字典
const typeOptions = ref([
  { value: "警告", label: "警告" },
  { value: "提示", label: "提示" },
  { value: "故障", label: "故障" },
]);

// 报警记录弹窗---表格加载状态
let loading = ref(true);

// 报警记录弹窗---表格列配置
const columns = [
  {
    title: "类别",
    dataIndex: "type",
    width: "6%",
  },
  {
    title: "设备名称",
    dataIndex: "deviceName",
    width: "10%",
    ellipsis: true,
  },
  {
    title: "故障内容",
    dataIndex: "content",
    width: "28%",
    ellipsis: true,
  },
  {
    title: "报警时间",
    dataIndex: "createTime",
    width: "8%",
    ellipsis: true,
  },
];

// 报警记录弹窗---表格数据源
let data = ref([]);

// 报警记录弹窗---查询条件
let formState = ref({
  type: undefined,
  deviceName: undefined,
  content: undefined,
});

// 报警时间区间
const alarmTime = ref([]);

// 报警记录弹窗---分页器配置项
let pagination = ref({
  current: 1,
  pageSize: 10,
  total: null,
  pageSizeOptions: ["5", "8", "10", "13"],
  showSizeChanger: true,
  showQuickJumper: true,
  responsive: true,
  showTotal: (total) => `总共 ${total} 条数据`,
});

// 类别颜色映射
const getCategoryClass = (category) => {
  switch (category) {
    case "警告":
      return "alarm-color-orange";
    case "提示":
      return "alarm-color-yellow";
    case "故障":
      return "alarm-color-red";
    default:
      return "";
  }
};

onMounted(() => {
  // 报警记录弹窗---展示默认的表格数据
  handleSearch();
});

/**
 * @introduction 报警记录弹窗---分页器变化的回调
 */
const pageChange = (page) => {
  pagination.value.current = page.current;
  pagination.value.pageSize = page.pageSize;
  handleSearch();
};

/**
 * @introduction 报警记录弹窗---查询事件
 */
const handleSearch = () => {
  let params = {
    page: pagination.value.current,
    limit: pagination.value.pageSize,
    ...formState.value,
    // 选择器只取到年月日，传参时开始日补 00:00:00、结束日补 23:59:59
    createTimeStart: alarmTime.value?.[0]
      ? `${alarmTime.value[0]} 00:00:00`
      : undefined,
    createTimeEnd: alarmTime.value?.[1]
      ? `${alarmTime.value[1]} 23:59:59`
      : undefined,
  };
  loading.value = true;
  apiTopHeader
    .getAlarmRecordPage(params)
    .then((res) => {
      if (res.data.code == 0) {
        loading.value = false;
        pagination.value.total = res.data.data.count;
        data.value = res.data.data.list;
      }
    })
    .catch((error) => {
      loading.value = false;
      console.error(error);
      message.error("获取报警记录失败", 2);
    });
};

/**
 * @introduction 报警记录弹窗---重置查询
 */
const resetSearch = () => {
  formState.value = {
    type: undefined,
    deviceName: undefined,
    content: undefined,
  };
  alarmTime.value = [];
  pagination.value.current = 1;
  handleSearch();
};

/**
 * @introduction 关闭弹窗
 */
const closeModal = () => {
  emits("update:visible", false);
};
</script>

<style lang="scss" scoped>
@use "../../../assets/style/modalcoustom.scss";

.alarm-color-red {
  color: #ff4d4f;
}
.alarm-color-orange {
  color: #fa8c16;
}
.alarm-color-yellow {
  color: #fadb14;
}

// allowClear 的输入框会被包裹在 .ant-input-affix-wrapper 中，需单独覆盖深色主题
:deep(.ant-input-affix-wrapper) {
  background-color: rgba(18, 27, 37, 0.8);
  border: 1px solid #9ecdfc;
}
:deep(.ant-input-affix-wrapper .ant-input) {
  background-color: transparent;
}

.search {
  display: flex;
  .search-left {
    flex: 1;
  }
  .search-right {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }
}

.modal-name {
  width: 100%;
  display: flex;
  justify-content: center;
}

:deep(.ant-pagination-total-text) {
  color: white;
}
:deep(.ant-pagination-options-quick-jumper) {
  color: white;
}
:deep(.ant-pagination-jump-prev) {
  display: none !important;
}
:deep(.ant-pagination-jump-next) {
  display: none !important;
}
:deep(.ant-checkbox-wrapper) {
  color: white;
}
</style>
